"use client"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

interface FieldRendererProps {
  fieldKey: string
  fieldType: { type: string; options?: string[] }
  value: any
  editable: boolean
  onChange: (value: any) => void
  id?: string
}

export function FieldRenderer({ fieldKey, fieldType, value, editable, onChange, id }: FieldRendererProps) {
  const label = fieldKey.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
  const fieldId = id || fieldKey

  if (!fieldType || !fieldType.type) {
    return (
      <div className="space-y-2">
        <Label htmlFor={fieldId}>{label}</Label>
        <Input
          id={fieldId}
          type="text"
          value={value || ""}
          disabled={!editable}
          onChange={editable ? (e) => onChange(e.target.value) : undefined}
        />
      </div>
    )
  }

  switch (fieldType.type) {
    case "checkbox":
      return (
        <div className="flex items-center space-x-2">
          <Checkbox
            id={fieldId}
            checked={value === true || value === "true"}
            disabled={!editable}
            onCheckedChange={editable ? onChange : undefined}
          />
          <Label htmlFor={fieldId}>{label}</Label>
        </div>
      )
    case "select":
      return (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>{label}</Label>
          <Select value={value} disabled={!editable} onValueChange={editable ? onChange : undefined}>
            <SelectTrigger id={fieldId}>
              <SelectValue
                placeholder={editable ? `Select ${label.toLowerCase()}` : value || `Select ${label.toLowerCase()}`}
              />
            </SelectTrigger>
            <SelectContent>
              {editable && fieldType.options ? (
                fieldType.options.map((option: string) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value={value}>{value}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      )
    case "textarea":
      return (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>{label}</Label>
          <Textarea
            id={fieldId}
            value={value}
            disabled={!editable}
            onChange={editable ? (e) => onChange(e.target.value) : undefined}
            className="resize-none"
          />
        </div>
      )
    default:
      return (
        <div className="space-y-2">
          <Label htmlFor={fieldId}>{label}</Label>
          <Input
            id={fieldId}
            type={fieldType.type}
            value={value}
            disabled={!editable}
            onChange={
              editable
                ? (e) => {
                    const newValue = fieldType.type === "number" ? Number(e.target.value) : e.target.value
                    onChange(newValue)
                  }
                : undefined
            }
          />
        </div>
      )
  }
}

